import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, getDoc, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import SetupGuard from '../components/SetupGuard';
import './AdminAccount.css';

const tableTypes = ['Regular', 'Bar', 'Booth', 'Private', 'Outdoor', 'Window'];
const tableShapes = ['rounded', 'square', 'rectangle', 'triangle'];

const FloorPlanPage = () => {
  const params = useParams();
  const [locationId, setLocationId] = useState(params.locationId);
  const navigate = useNavigate();
  const auth = getAuth();
  
  const [user, setUser] = useState(null);
  const [tables, setTables] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState(0);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [localPositions, setLocalPositions] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [showManageSectionsModal, setShowManageSectionsModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editingTable, setEditingTable] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        if (!locationId) {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const selectedId = userDoc.data().selectedLocationId;
            if (selectedId) {
              setLocationId(selectedId);
            } else {
              Swal.fire('Info', 'Please select a location first', 'info');
              navigate('/admin/locations');
              return;
            }
          }
        }
        if (locationId) {
          await loadData(currentUser.uid);
        }
      }
      // NOTE: no `else navigate('/admin/login')` here — ProtectedRoute already
      // guards auth. Bouncing on a momentary null (session still hydrating)
      // kicked users to login when entering FloorPlan on a fresh load.
    });

    return () => unsubscribe();
  }, [locationId]);

  const loadData = async (uid) => {
    try {
      setLoading(true);
      const db = getFirestore();
      
      const sectionsSnapshot = await getDocs(
        query(collection(db, 'users', uid, 'locations', locationId, 'sections'), orderBy('order'))
      );
      const sectionsData = sectionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSections(sectionsData);
      
      const tablesSnapshot = await getDocs(collection(db, 'users', uid, 'locations', locationId, 'tables'));
      let tablesData = tablesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      if (sectionsData.length > 0) {
        const orphanedTables = tablesData.filter(t => !t.sectionId);
        for (const table of orphanedTables) {
          await deleteDoc(doc(db, 'users', uid, 'locations', locationId, 'tables', table.id));
        }
        if (orphanedTables.length > 0) {
          const freshSnapshot = await getDocs(collection(db, 'users', uid, 'locations', locationId, 'tables'));
          tablesData = freshSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }
      }
      
      setTables(tablesData);
    } catch (error) {
      console.error('Error loading floor plan data:', error);
      Swal.fire('Error', 'Failed to load floor plan data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSection = async (formValues) => {
    if (!user || !locationId) return;
    
    try {
      const db = getFirestore();
      if (editingSection) {
        await updateDoc(doc(db, 'users', user.uid, 'locations', locationId, 'sections', editingSection.id), {
          name: formValues.name,
          order: formValues.order,
        });
      } else {
        await setDoc(doc(collection(db, 'users', user.uid, 'locations', locationId, 'sections')), {
          name: formValues.name,
          order: formValues.order,
          createdAt: serverTimestamp(),
        });
      }
      
      setShowSectionModal(false);
      setEditingSection(null);
      await loadData(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire('Success', `Section ${editingSection ? 'updated' : 'created'}`, 'success');
    } catch (error) {
      console.error('Error saving section:', error);
      Swal.fire('Error', 'Failed to save section', 'error');
    }
  };

  const handleDeleteSection = async (section) => {
    const tablesInSection = tables.filter(t => t.sectionId === section.id);
    if (tablesInSection.length > 0) {
      Swal.fire('Error', `Cannot delete section with ${tablesInSection.length} tables`, 'error');
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Section?',
      text: `Are you sure you want to delete "${section.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });

    if (result.isConfirmed && user) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'users', user.uid, 'locations', locationId, 'sections', section.id));
        await loadData(user.uid);
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        Swal.fire('Deleted', 'Section deleted', 'success');
      } catch (error) {
        console.error('Error deleting section:', error);
        Swal.fire('Error', 'Failed to delete section', 'error');
      }
    }
  };

  const handleSaveTable = async (formValues) => {
    if (!user || !locationId) return;
    
    try {
      const db = getFirestore();
      if (editingTable) {
        await updateDoc(doc(db, 'users', user.uid, 'locations', locationId, 'tables', editingTable.id), {
          name: formValues.name,
          sectionId: formValues.sectionId,
          shape: formValues.shape,
          type: formValues.type,
          maxPartySize: formValues.maxPartySize,
        });
      } else {
        await setDoc(doc(collection(db, 'users', user.uid, 'locations', locationId, 'tables')), {
          name: formValues.name,
          sectionId: formValues.sectionId,
          shape: formValues.shape,
          type: formValues.type,
          maxPartySize: formValues.maxPartySize,
          positionX: 50,
          positionY: 50,
          createdAt: serverTimestamp(),
        });
      }
      
      setShowTableModal(false);
      setEditingTable(null);
      await loadData(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire('Success', `Table ${editingTable ? 'updated' : 'created'}`, 'success');
    } catch (error) {
      console.error('Error saving table:', error);
      Swal.fire('Error', 'Failed to save table', 'error');
    }
  };

  // ── Table QR (order-at-table) ────────────────────────────────────────────
  const [qrModal, setQrModal] = useState(null); // { table, dataUrl, url }
  // Branded QR: high error-correction + the location logo in the center +
  // the table name underneath (one printable image).
  const handleShowTableQr = async (table) => {
    const base = process.env.REACT_APP_PUBLIC_WEB_URL || 'https://go.fodyfee.com';
    const url = `${base}/t/${locationId}/${table.id}`;
    const qrData = await QRCode.toDataURL(url, { width: 640, margin: 1, errorCorrectionLevel: 'H', color: { dark: '#1a1a1a', light: '#ffffff' } });

    let logoUrl = null;
    try {
      const db = getFirestore();
      const locSnap = await getDoc(doc(db, 'users', user.uid, 'locations', locationId));
      logoUrl = locSnap.data()?.logoUrl || null;
    } catch (e) { /* logo optional */ }

    const dataUrl = await new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 640; canvas.height = 780; // QR + caption band
      const cx = canvas.getContext('2d');
      cx.fillStyle = '#ffffff';
      cx.fillRect(0, 0, 640, 780);
      const qrImg = new Image();
      qrImg.onload = () => {
        cx.drawImage(qrImg, 0, 0, 640, 640);
        const finish = () => {
          // table name caption
          cx.fillStyle = '#1a1a1a';
          cx.font = 'bold 44px Arial, sans-serif';
          cx.textAlign = 'center';
          cx.fillText(table.name || 'Table', 320, 722);
          resolve(canvas.toDataURL('image/png'));
        };
        const drawLogo = (src) => {
          const logo = new Image();
          logo.crossOrigin = 'anonymous';
          logo.onload = () => {
            const box = 150, x = (640 - box) / 2, y = (640 - box) / 2;
            // white rounded patch so the QR stays scannable
            cx.fillStyle = '#ffffff';
            cx.beginPath();
            if (cx.roundRect) cx.roundRect(x - 10, y - 10, box + 20, box + 20, 24); else cx.rect(x - 10, y - 10, box + 20, box + 20);
            cx.fill();
            // keep aspect
            const scale = Math.min(box / logo.width, box / logo.height);
            const w = logo.width * scale, h = logo.height * scale;
            cx.drawImage(logo, 320 - w / 2, 320 - h / 2, w, h);
            finish();
          };
          logo.onerror = finish;
          logo.src = src;
        };
        if (logoUrl) drawLogo(logoUrl); else finish();
      };
      qrImg.src = qrData;
    });
    setQrModal({ table, dataUrl, url });
  };
  const handlePrintTableQr = () => {
    if (!qrModal) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>Table QR — ${qrModal.table.name || ''}</title>
      <style>body{font-family:Arial,sans-serif;text-align:center;padding:40px}img{width:320px}h1{font-size:28px;margin:0 0 4px}p{color:#666;margin:4px 0}.big{font-size:20px;font-weight:700;margin-top:12px}</style>
      </head><body>
      <h1>${qrModal.table.name || 'Table'}</h1>
      <p>Scan to view the menu & order</p>
      <img src="${qrModal.dataUrl}" />
      <p class="big">Scan → Order → Pay at the register</p>
      <p style="font-size:11px;color:#999;word-break:break-all">${qrModal.url}</p>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const handleDeleteTable = async (table) => {
    const result = await Swal.fire({
      title: 'Delete Table?',
      text: `Are you sure you want to delete "${table.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });

    if (result.isConfirmed && user) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'users', user.uid, 'locations', locationId, 'tables', table.id));
        await loadData(user.uid);
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        Swal.fire('Deleted', 'Table deleted', 'success');
      } catch (error) {
        console.error('Error deleting table:', error);
        Swal.fire('Error', 'Failed to delete table', 'error');
      }
    }
  };

  const handleSaveTablePosition = async (table) => {
    const position = localPositions[table.id];
    if (!position || !user) return;
    
    const originalX = table.positionX || 50;
    const originalY = table.positionY || 50;
    
    if (position.x !== originalX || position.y !== originalY) {
      try {
        const db = getFirestore();
        await updateDoc(doc(db, 'users', user.uid, 'locations', locationId, 'tables', table.id), {
          positionX: position.x,
          positionY: position.y,
        });
        await loadData(user.uid);
      } catch (error) {
        console.error('Error saving position:', error);
      }
    }
    setLocalPositions(prev => {
      const updated = { ...prev };
      delete updated[table.id];
      return updated;
    });
  };

  const handleDragStart = (table, e) => {
    setLocalPositions(prev => ({
      ...prev,
      [table.id]: { x: table.positionX || 50, y: table.positionY || 50 }
    }));
    setIsDragging(true);
  };

  const handleDrag = (table, e) => {
    if (!isDragging || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left - 30, rect.width - 60));
    const y = Math.max(0, Math.min(e.clientY - rect.top - 30, rect.height - 60));
    
    setLocalPositions(prev => ({
      ...prev,
      [table.id]: { x, y }
    }));
  };

  const handleDragEnd = (table) => {
    setIsDragging(false);
    handleSaveTablePosition(table);
  };

  const filteredTables = selectedSectionId 
    ? tables.filter(t => t.sectionId === selectedSectionId)
    : tables;

  const getBorderRadius = (shape) => {
    switch (shape) {
      case 'rounded': return '50%';
      case 'square': return '4px';
      case 'rectangle': return '4px';
      case 'triangle': return '0';
      default: return '50%';
    }
  };

  const openAddSection = () => {
    setEditingSection(null);
    setShowSectionModal(true);
  };

  const openEditSection = (section) => {
    setEditingSection(section);
    setShowSectionModal(true);
  };

  const openAddTable = () => {
    if (sections.length === 0) {
      Swal.fire('Error', 'Please add a section first', 'error');
      return;
    }
    setEditingTable(null);
    setShowTableModal(true);
  };

  const openEditTable = (table) => {
    setEditingTable(table);
    setShowTableModal(true);
  };

  if (loading) {
    return (
      <AdminLayout title="Floor Plan">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Floor Plan">
      <SetupGuard prerequisites={['location']}>
      <div className="admin-account-page">
        {/* View Toggle */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex" style={{ background: '#f8f9fa', borderRadius: '12px', padding: '4px' }}>
              <button
                className={`btn flex-fill ${selectedView === 0 ? 'btn-primary' : 'btn-light'}`}
                onClick={() => setSelectedView(0)}
                style={{ borderRadius: '10px' }}
              >
                <i className="fas fa-list mr-2"></i>List View
              </button>
              <button
                className={`btn flex-fill ${selectedView === 1 ? 'btn-primary' : 'btn-light'}`}
                onClick={() => {
                  if (selectedSectionId || sections.length === 0) {
                    setSelectedView(1);
                  } else {
                    Swal.fire('Info', 'Please select a section first', 'info');
                  }
                }}
                style={{ borderRadius: '10px' }}
              >
                <i className="fas fa-th-large mr-2"></i>Floor Plan
              </button>
            </div>
          </div>
        </div>

        {/* Section Selector */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex align-items-center overflow-auto pb-2">
              {(selectedView === 0 || sections.length === 0) && (
                <button
                  className={`btn btn-sm mr-2 ${selectedSectionId === null ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setSelectedSectionId(null)}
                >
                  All
                </button>
              )}
              {sections.map(section => (
                <button
                  key={section.id}
                  className={`btn btn-sm mr-2 ${selectedSectionId === section.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setSelectedSectionId(section.id)}
                >
                  {section.name}
                </button>
              ))}
              <button
                className="btn btn-sm btn-outline-primary mr-2"
                onClick={openAddSection}
              >
                <i className="fas fa-plus mr-1"></i>Add
              </button>
              {selectedView === 0 && (
                <button
                  className="btn btn-sm btn-outline-info"
                  onClick={() => setShowManageSectionsModal(true)}
                >
                  <i className="fas fa-cog mr-1"></i>Manage
                </button>
              )}
            </div>
            <div className="mt-2">
              <button className="btn btn-primary btn-sm" onClick={openAddTable}>
                <i className="fas fa-plus mr-1"></i>Add Table
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {selectedView === 0 ? (
          /* List View */
          <div className="row">
            {filteredTables.length === 0 ? (
              <div className="col-12 text-center py-5">
                <i className="fas fa-table fa-4x text-muted mb-3"></i>
                <h5 className="text-muted">No tables yet</h5>
                <button className="btn btn-primary mt-3" onClick={openAddTable}>
                  <i className="fas fa-plus mr-2"></i>Add Table
                </button>
              </div>
            ) : (
              filteredTables.map(table => (
                <div key={table.id} className="col-md-6 col-lg-4 mb-3">
                  <div className="card">
                    <div className="card-body d-flex align-items-center">
                      <div
                        className="d-flex align-items-center justify-content-center mr-3"
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 10,
                          background: 'rgba(255,107,53,0.1)',
                          color: '#ff6b35',
                        }}
                      >
                        <i className="fas fa-chair"></i>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="m-0 font-weight-bold">{table.name}</h6>
                        <small className="text-muted">
                          {table.type} • {table.shape} • Up to {table.maxPartySize}
                        </small>
                      </div>
                      <div className="position-relative">
                        <button 
                          className="btn btn-sm btn-link" 
                          onClick={() => setOpenDropdownId(openDropdownId === table.id ? null : table.id)}
                        >
                          <i className="fas fa-ellipsis-v"></i>
                        </button>
                        {openDropdownId === table.id && (
                          <div 
                            className="card position-absolute" 
                            style={{ 
                              right: 0, 
                              top: '100%', 
                              zIndex: 1000, 
                              minWidth: '120px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                            }}
                          >
                            <div className="list-group list-group-flush">
                              <button 
                                className="list-group-item list-group-item-action py-2" 
                                onClick={() => { setOpenDropdownId(null); openEditTable(table); }}
                              >
                                <i className="fas fa-edit mr-2 text-primary"></i>Edit
                              </button>
                              <button 
                                className="list-group-item list-group-item-action py-2" 
                                onClick={() => { setOpenDropdownId(null); handleShowTableQr(table); }}
                              >
                                <i className="fas fa-qrcode mr-2 text-secondary"></i>Table QR (order-at-table)
                              </button>
                              <button 
                                className="list-group-item list-group-item-action py-2 text-danger" 
                                onClick={() => { setOpenDropdownId(null); handleDeleteTable(table); }}
                              >
                                <i className="fas fa-trash mr-2"></i>Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* Floor Plan Canvas */
          <div className="row">
            <div className="col-12">
              <div 
                ref={canvasRef}
                className="card"
                style={{ 
                  height: '600px', 
                  background: '#f8f9fa',
                  backgroundImage: 'linear-gradient(#e0e0e0 1px, transparent 1px), linear-gradient(90deg, #e0e0e0 1px, transparent 1px)',
                  backgroundSize: '50px 50px',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: isDragging ? 'grabbing' : 'default'
                }}
                onMouseUp={() => setIsDragging(false)}
                onMouseLeave={() => setIsDragging(false)}
              >
                {filteredTables.length === 0 ? (
                  <div className="d-flex flex-column justify-content-center align-items-center h-100">
                    <i className="fas fa-table fa-4x text-muted mb-3"></i>
                    <h5 className="text-muted">No tables in this section</h5>
                  </div>
                ) : (
                  filteredTables.map(table => {
                    const pos = localPositions[table.id] || { x: table.positionX || 50, y: table.positionY || 50 };
                    const size = table.shape === 'rectangle' ? { w: 80, h: 50 } : { w: 60, h: 60 };
                    
                    return (
                      <div
                        key={table.id}
                        style={{
                          position: 'absolute',
                          left: pos.x,
                          top: pos.y,
                          width: size.w,
                          height: size.h,
                          background: '#ff6b35',
                          borderRadius: getBorderRadius(table.shape),
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'grab',
                          userSelect: 'none',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          zIndex: localPositions[table.id] ? 100 : 1,
                        }}
                        onMouseDown={(e) => handleDragStart(table, e)}
                        onMouseMove={(e) => handleDrag(table, e)}
                        onMouseUp={() => handleDragEnd(table)}
                        onClick={() => !isDragging && openEditTable(table)}
                        title={`${table.name} - ${table.type} - Max ${table.maxPartySize}`}
                      >
                        <span>{table.name}</span>
                        <span style={{ fontSize: '8px', opacity: 0.9 }}>{table.maxPartySize}</span>
                      </div>
                    );
                  })
                )}
              </div>
              <p className="text-muted mt-2 text-center">
                <small><i className="fas fa-info-circle mr-1"></i>Drag tables to reposition • Click to edit</small>
              </p>
            </div>
          </div>
        )}

        {/* Section Modal */}
        {showSectionModal && (
          <SectionFormModal
            section={editingSection}
            sections={sections}
            onSave={handleSaveSection}
            onClose={() => { setShowSectionModal(false); setEditingSection(null); }}
          />
        )}

        {/* Table Modal */}
        {showTableModal && (
          <TableFormModal
            table={editingTable}
            sections={sections}
            selectedSectionId={selectedSectionId}
            onSave={handleSaveTable}
            onClose={() => { setShowTableModal(false); setEditingTable(null); }}
          />
        )}

        {/* Manage Sections Modal */}
        {showManageSectionsModal && (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title font-weight-bold">Manage Sections</h5>
                  <button type="button" className="close" onClick={() => setShowManageSectionsModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {sections.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">No sections yet</p>
                    </div>
                  ) : (
                    sections.map(s => (
                      <div key={s.id} className="d-flex justify-content-between align-items-center p-2 border-bottom">
                        <span className="font-weight-bold">{s.name}</span>
                        <div>
                          <button className="btn btn-sm btn-outline-primary mr-1" onClick={() => { setShowManageSectionsModal(false); openEditSection(s); }}>
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => { setShowManageSectionsModal(false); handleDeleteSection(s); }}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowManageSectionsModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </SetupGuard>
          {/* Table QR modal */}
      {qrModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} onClick={() => setQrModal(null)}>
          <div className="modal-dialog modal-sm modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ borderRadius: 16 }}>
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold"><i className="fas fa-qrcode mr-2"></i>{qrModal.table.name || 'Table'} — order-at-table QR</h5>
                <button type="button" className="close" onClick={() => setQrModal(null)}><span>&times;</span></button>
              </div>
              <div className="modal-body text-center">
                <img src={qrModal.dataUrl} alt="Table QR" style={{ width: '100%', maxWidth: 260 }} />
                <p className="text-muted mt-2 mb-0" style={{ fontSize: 12, wordBreak: 'break-all' }}>{qrModal.url}</p>
                <p className="text-muted mt-2" style={{ fontSize: 12 }}>Customers scan → menu → order lands as an open tab for this table (pay at the register). If staff opened a tab, guests recover it with their 4-digit number.</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setQrModal(null)}>Close</button>
                <button className="btn btn-primary" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={handlePrintTableQr}><i className="fas fa-print mr-1"></i>Print</button>
              </div>
            </div>
          </div>
        </div>
      )}
</AdminLayout>
  );
};

const SectionFormModal = ({ section, sections, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: section?.name || '',
    order: section?.order ?? sections.length,
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Please enter a section name';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">{section ? 'Edit Section' : 'Add Section'}</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label>Section Name *</label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 100 }}
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}>
                {section ? 'Update' : 'Create'} Section
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const TableFormModal = ({ table, sections, selectedSectionId, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: table?.name || '',
    sectionId: table?.sectionId || selectedSectionId || (sections[0]?.id || ''),
    shape: table?.shape || 'rounded',
    type: table?.type || 'Regular',
    maxPartySize: table?.maxPartySize || 4,
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Please enter a table name';
    if (!formData.sectionId) errs.sectionId = 'Please select a section (create one first)';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">{table ? 'Edit Table' : 'Add Table'}</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label>Table Name *</label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              <div className="form-group">
                <label>Section</label>
                <select
                  className="form-control"
                  value={formData.sectionId}
                  onChange={(e) => setFormData({ ...formData, sectionId: e.target.value })}
                >
                  {sections.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="d-block">Shape</label>
                <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                  {tableShapes.map(s => {
                    const isSelected = formData.shape === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{
                          borderRadius: '8px',
                          backgroundColor: isSelected ? '#ff6b35' : undefined,
                          borderColor: isSelected ? '#ff6b35' : undefined,
                        }}
                        onClick={() => setFormData({ ...formData, shape: s })}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label>Table Type</label>
                <select
                  className="form-control"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  {tableTypes.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Max Party Size</label>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 100 }}
                  value={formData.maxPartySize}
                  onChange={(e) => setFormData({ ...formData, maxPartySize: parseInt(e.target.value) || 4 })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}>
                {table ? 'Update' : 'Create'} Table
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FloorPlanPage;
